import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';

const COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  dark: '#1e293b',
};

const DISCIPLINE_COLORS = {
  'Piping': '#8b5cf6',
  'Structural': '#10b981',
  'Mechanical': '#2563eb',
  'Vessels': '#7c3aed',
  'Conveyors': '#059669',
  'Chutes': '#d97706',
  'Skids': '#dc2626',
  'HVAC': '#06b6d4',
  'Ducts': '#ec4899',
  'Boilermaker': '#ef6c00',
  'Civil': '#78716c',
  'Electrical': '#eab308',
};

// Credit is reported as two independent phases that are never summed (summing
// double-counts to >100%). Disciplines with a fab phase (piping) expose
// earned_credit_shop/earned_credit_field; single-phase disciplines (equipment)
// have no shop fab credit, so their plain earned_credit maps to Field (installation).
const rowShopCredit = (r) => parseFloat(r.earned_credit_shop) || 0;
const rowFieldCredit = (r) => (
  r.earned_credit_field !== undefined && r.earned_credit_field !== null
    ? parseFloat(r.earned_credit_field) || 0
    : parseFloat(r.earned_credit) || 0
);

const SOVReport = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('consolidated');
  const [matrixMeasure, setMatrixMeasure] = useState('credit');
  const [sovViewMode, setSovViewMode] = useState('internal'); // client = credits; internal = hours + credits
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchSOVData();
    }
  }, [projectId]);

  const fetchSOVData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/sov-report/project/${projectId}`);
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch SOV data');
      }
    } catch (err) {
      console.error('Error fetching SOV report:', err);
      setError('Failed to load SOV report data');
    } finally {
      setLoading(false);
    }
  };

  const disciplineTabs = useMemo(() => {
    if (!data?.byDiscipline) return [];
    return data.byDiscipline.map(d => ({
      id: d.discipline.toLowerCase(),
      label: d.discipline,
      rows: d.rows,
    }));
  }, [data]);

  const fmtNum = (v) => Math.round(parseFloat(v) || 0).toLocaleString('en-US');
  const fmtHrs = (v) => {
    const n = parseFloat(v) || 0;
    return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  };
  const pct = (earned, total) => {
    const t = parseFloat(total) || 0;
    const e = parseFloat(earned) || 0;
    return t > 0 ? Math.round((e / t) * 1000) / 10 : 0;
  };

  const getProgressColor = (val) => val >= 100 ? COLORS.success : val >= 50 ? COLORS.warning : val > 0 ? '#f59e0b' : '#94a3b8';

  const thS = { padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const tdS = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '13px' };

  const buildPdf = useCallback(async () => {
    if (!data || !data.consolidated || data.consolidated.length === 0) return null;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const f = (v) => { const n = parseFloat(v) || 0; return n.toLocaleString('en-US', { maximumFractionDigits: 1 }); };
    const p = (e, t) => { const tv = parseFloat(t) || 0; return tv > 0 ? (Math.round((parseFloat(e) / tv) * 1000) / 10) + '%' : '0%'; };
    const { y: letterheadY, resolved } = await applyLetterhead(doc, { instance, accounts, projectId, margin: 14 });
    const job = resolved?.context?.job_number;

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('SOV Summary Report', pageW / 2, letterheadY, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${now}`, pageW / 2, letterheadY + 6, { align: 'center' });

    const showHours = sovViewMode === 'internal';
    const s = data.summary;
    doc.setFontSize(9);
    const hourSummary = showHours
      ? `   |   Total Hrs: ${f(s.total_budget_hrs)}   |   Earned Hrs: ${f(s.total_earned_hrs)} (${s.hrs_progress}%)`
      : '';
    doc.text(`${showHours ? 'Internal view (hours + credits)' : 'Client view (credits)'}   |   SOV Codes: ${s.total_sov_codes}   |   Items: ${fmtNum(s.total_items)}${hourSummary}   |   Total Credit: ${f(s.total_credit)}   |   Credit: Shop ${s.shop_credit_progress ?? 0}% / Field ${s.field_credit_progress ?? 0}%`, pageW / 2, letterheadY + 12, { align: 'center' });

    // Consolidated table (credit split into Shop/Field, never summed)
    const tableBody = [];
    data.consolidated.forEach(r => {
      const bh = parseFloat(r.total_budget_hrs) || 0;
      const eh = parseFloat(r.earned_hrs) || 0;
      const tc = parseFloat(r.total_credit) || 0;
      const sc = rowShopCredit(r);
      const fc = rowFieldCredit(r);
      const discStr = (r.disciplines || []).map(d => `${d.name}(${d.count})`).join(', ');
      tableBody.push([
        r.sov_code,
        discStr,
        fmtNum(r.total_items),
        ...(showHours ? [f(bh), f(eh), p(eh, bh)] : []),
        f(tc), tc > 0 ? p(sc, tc) : '—', tc > 0 ? p(fc, tc) : '—',
      ]);
    });

    const totBH = data.consolidated.reduce((a, r) => a + (parseFloat(r.total_budget_hrs) || 0), 0);
    const totEH = data.consolidated.reduce((a, r) => a + (parseFloat(r.earned_hrs) || 0), 0);
    const totC = data.consolidated.reduce((a, r) => a + (parseFloat(r.total_credit) || 0), 0);
    const totSC = data.consolidated.reduce((a, r) => a + rowShopCredit(r), 0);
    const totFC = data.consolidated.reduce((a, r) => a + rowFieldCredit(r), 0);
    const totI = data.consolidated.reduce((a, r) => a + (parseInt(r.total_items) || 0), 0);

    tableBody.push([
      { content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      { content: `${disciplineTabs.length} disc.`, styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
      ...[fmtNum(totI), ...(showHours ? [f(totBH), f(totEH), p(totEH, totBH)] : []), f(totC), p(totSC, totC), p(totFC, totC)].map(v => ({
        content: v, styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] }
      })),
    ]);

    autoTable(doc, {
      startY: letterheadY + 16,
      head: [['SOV Code', 'Disciplines', 'Items', ...(showHours ? ['Total Hrs', 'Earned Hrs', 'Hrs %'] : []), 'Total Credit', 'Shop (Fab) %', 'Field (Install) %']],
      body: tableBody,
      styles: { fontSize: 8, cellPadding: 2, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left' }, 1: { halign: 'left', fontSize: 7 } },
      theme: 'grid',
    });

    // Per-discipline pages
    data.byDiscipline.forEach(disc => {
      doc.addPage();
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${disc.discipline} - SOV Summary`, pageW / 2, 14, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${now}`, pageW / 2, 20, { align: 'center' });

      const sf = !!disc.hasShopField;
      const dBody = [];
      disc.rows.forEach(r => {
        const bh = parseFloat(r.total_budget_hrs) || 0;
        const eh = parseFloat(r.earned_hrs) || 0;
        const tc = parseFloat(r.total_credit) || 0;
        const ec = parseFloat(r.earned_credit) || 0;
        const creditCols = sf
          ? [f(tc), tc > 0 ? p(rowShopCredit(r), tc) : '—', tc > 0 ? p(rowFieldCredit(r), tc) : '—']
          : [f(tc), f(ec), p(ec, tc)];
        dBody.push([
          r.sov_code,
          fmtNum(r.item_count),
          ...(showHours ? [f(bh), f(eh), p(eh, bh)] : []),
          ...creditCols,
        ]);
      });

      const dTotBH = disc.rows.reduce((a, r) => a + (parseFloat(r.total_budget_hrs) || 0), 0);
      const dTotEH = disc.rows.reduce((a, r) => a + (parseFloat(r.earned_hrs) || 0), 0);
      const dTotC = disc.rows.reduce((a, r) => a + (parseFloat(r.total_credit) || 0), 0);
      const dTotEC = disc.rows.reduce((a, r) => a + (parseFloat(r.earned_credit) || 0), 0);
      const dTotSC = disc.rows.reduce((a, r) => a + rowShopCredit(r), 0);
      const dTotFC = disc.rows.reduce((a, r) => a + rowFieldCredit(r), 0);
      const dTotI = disc.rows.reduce((a, r) => a + (parseInt(r.item_count) || 0), 0);

      const dTotCreditCols = sf
        ? [f(dTotC), p(dTotSC, dTotC), p(dTotFC, dTotC)]
        : [f(dTotC), f(dTotEC), p(dTotEC, dTotC)];
      dBody.push([
        { content: 'Total', styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] } },
        ...[fmtNum(dTotI), ...(showHours ? [f(dTotBH), f(dTotEH), p(dTotEH, dTotBH)] : []), ...dTotCreditCols].map(v => ({
          content: v, styles: { fontStyle: 'bold', fillColor: [30, 41, 59], textColor: [255, 255, 255] }
        })),
      ]);

      autoTable(doc, {
        startY: 24,
        head: [sf
          ? ['SOV Code', 'Items', ...(showHours ? ['Total Hrs', 'Earned Hrs', 'Hrs %'] : []), 'Total Credit', 'Shop (Fab) %', 'Field (Install) %']
          : ['SOV Code', 'Items', ...(showHours ? ['Total Hrs', 'Earned Hrs', 'Hrs %'] : []), 'Total Credit', 'Earned Credit', 'Credit %']],
        body: dBody,
        styles: { fontSize: 8, cellPadding: 2, halign: 'center' },
        headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
        columnStyles: { 0: { halign: 'left' } },
        theme: 'grid',
      });
    });

    doc.__exportJob = job;
    return doc;
  }, [data, disciplineTabs, instance, accounts, projectId, sovViewMode]);

  const handleDownloadPdf = useCallback(async () => {
    const doc = await buildPdf();
    if (doc) doc.save(exportFilename({ job: doc.__exportJob || projectId, reportSlug: 'SOV_Summary', ext: 'pdf' }));
  }, [buildPdf, projectId]);

  const handleSendEmail = useCallback(async () => {
    if (!emailAddresses.trim()) return;
    const doc = await buildPdf();
    if (!doc) return;
    setEmailSending(true);
    setEmailResult(null);
    try {
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const recipients = emailAddresses.split(/[,;\s]+/).filter(e => e.includes('@'));
      if (recipients.length === 0) {
        setEmailResult({ type: 'error', message: 'No valid email addresses' });
        setEmailSending(false);
        return;
      }
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: 'SOV Summary Report',
        htmlBody: `<p>Please find attached the SOV Summary Report.</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename: exportFilename({ job: doc.__exportJob || projectId, reportSlug: 'SOV_Summary', ext: 'pdf' }), fileblob: pdfBase64 }],
        feature: 'sov_report',
      });
      if (response.data.success) {
        setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s)` });
        setTimeout(() => { setShowEmailModal(false); setEmailResult(null); setEmailAddresses(''); }, 2000);
      } else {
        setEmailResult({ type: 'error', message: response.data.error || 'Failed to send' });
      }
    } catch (error) {
      setEmailResult({ type: 'error', message: error.response?.data?.error || 'Failed to send email' });
    } finally {
      setEmailSending(false);
    }
  }, [emailAddresses, buildPdf, instance, accounts]);

  const MetricCard = ({ title, value, subtitle, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: COLORS.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const renderTable = (rows, showDiscipline = false, showShopField = false) => {
    const totBudgetHrs = rows.reduce((s, r) => s + (parseFloat(r.total_budget_hrs) || 0), 0);
    const totEarnedHrs = rows.reduce((s, r) => s + (parseFloat(r.earned_hrs) || 0), 0);
    const totCredit = rows.reduce((s, r) => s + (parseFloat(r.total_credit) || 0), 0);
    const totEarnedCredit = rows.reduce((s, r) => s + (parseFloat(r.earned_credit) || 0), 0);
    const totShopCredit = rows.reduce((s, r) => s + rowShopCredit(r), 0);
    const totFieldCredit = rows.reduce((s, r) => s + rowFieldCredit(r), 0);
    const totItems = rows.reduce((s, r) => s + (parseInt(r.total_items || r.item_count) || 0), 0);
    const showHours = sovViewMode === 'internal';
    const barPctFor = (hrsPct, crPct, fieldPct) => (showHours ? hrsPct : (showShopField ? fieldPct : crPct));
    const totBarPct = showHours
      ? pct(totEarnedHrs, totBudgetHrs)
      : (showShopField ? pct(totFieldCredit, totCredit) : pct(totEarnedCredit, totCredit));
    const barLabel = showHours ? 'Progress (Hrs)' : (showShopField ? 'Progress (Field)' : 'Progress (Credit)');

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ ...thS, textAlign: 'left' }}>SOV Code</th>
                {showDiscipline && <th style={thS}>Disciplines</th>}
                <th style={thS}>Items</th>
                {showHours && (
                  <>
                    <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Total Hrs</th>
                    <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Earned Hrs</th>
                    <th style={{ ...thS, backgroundColor: '#eff6ff' }}>Hrs %</th>
                  </>
                )}
                <th style={{ ...thS, backgroundColor: '#fefce8' }}>Total Credit</th>
                {showShopField ? (
                  <>
                    <th style={{ ...thS, backgroundColor: '#fefce8' }}>Shop (Fab) %</th>
                    <th style={{ ...thS, backgroundColor: '#fefce8' }}>Field (Install) %</th>
                  </>
                ) : (
                  <>
                    <th style={{ ...thS, backgroundColor: '#fefce8' }}>Earned Credit</th>
                    <th style={{ ...thS, backgroundColor: '#fefce8' }}>Credit %</th>
                  </>
                )}
                <th style={{ ...thS, width: '160px' }}>{barLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const budgetHrs = parseFloat(r.total_budget_hrs) || 0;
                const earnedHrs = parseFloat(r.earned_hrs) || 0;
                const hrsPct = pct(earnedHrs, budgetHrs);
                const credit = parseFloat(r.total_credit) || 0;
                const earnedCr = parseFloat(r.earned_credit) || 0;
                const crPct = pct(earnedCr, credit);
                const shopPct = pct(rowShopCredit(r), credit);
                const fieldPct = pct(rowFieldCredit(r), credit);
                return (
                  <tr key={r.sov_code + idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: '600', color: r.sov_code === 'Unassigned' ? '#94a3b8' : '#1e293b' }}>
                      {r.sov_code}
                    </td>
                    {showDiscipline && (
                      <td style={tdS}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {(r.disciplines || []).map((d, i) => (
                            <span key={i} style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                              backgroundColor: DISCIPLINE_COLORS[d.name] ? `${DISCIPLINE_COLORS[d.name]}15` : '#f3f4f6',
                              color: DISCIPLINE_COLORS[d.name] || '#374151',
                            }}>
                              {d.name} ({d.count})
                            </span>
                          ))}
                        </div>
                      </td>
                    )}
                    <td style={tdS}>{fmtNum(r.total_items || r.item_count)}</td>
                    {showHours && (
                      <>
                        <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtHrs(budgetHrs)}</td>
                        <td style={{ ...tdS, backgroundColor: '#f8faff' }}>{fmtHrs(earnedHrs)}</td>
                        <td style={{ ...tdS, backgroundColor: '#f8faff', fontWeight: '600', color: getProgressColor(hrsPct) }}>{hrsPct}%</td>
                      </>
                    )}
                    <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtHrs(credit)}</td>
                    {showShopField ? (
                      <>
                        <td style={{ ...tdS, backgroundColor: '#fffef5', fontWeight: '600', color: credit > 0 ? getProgressColor(shopPct) : '#cbd5e1' }}>{credit > 0 ? `${shopPct}%` : '—'}</td>
                        <td style={{ ...tdS, backgroundColor: '#fffef5', fontWeight: '600', color: credit > 0 ? getProgressColor(fieldPct) : '#cbd5e1' }}>{credit > 0 ? `${fieldPct}%` : '—'}</td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...tdS, backgroundColor: '#fffef5' }}>{fmtHrs(earnedCr)}</td>
                        <td style={{ ...tdS, backgroundColor: '#fffef5', fontWeight: '600', color: getProgressColor(crPct) }}>{crPct}%</td>
                      </>
                    )}
                    <td style={{ ...tdS, padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '16px', backgroundColor: '#e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(barPctFor(hrsPct, crPct, fieldPct), 100)}%`, height: '100%', backgroundColor: getProgressColor(barPctFor(hrsPct, crPct, fieldPct)), borderRadius: '8px', transition: 'width 0.3s ease' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b', minWidth: '32px', textAlign: 'right' }}>{barPctFor(hrsPct, crPct, fieldPct)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                <td style={{ padding: '10px 12px', textAlign: 'left' }}>Grand Total</td>
                {showDiscipline && <td style={{ padding: '10px', textAlign: 'center' }}>{disciplineTabs.length} disc.</td>}
                <td style={{ padding: '10px', textAlign: 'center' }}>{fmtNum(totItems)}</td>
                {showHours && (
                  <>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmtHrs(totBudgetHrs)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmtHrs(totEarnedHrs)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{pct(totEarnedHrs, totBudgetHrs)}%</td>
                  </>
                )}
                <td style={{ padding: '10px', textAlign: 'center' }}>{fmtHrs(totCredit)}</td>
                {showShopField ? (
                  <>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{pct(totShopCredit, totCredit)}%</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{pct(totFieldCredit, totCredit)}%</td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmtHrs(totEarnedCredit)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{pct(totEarnedCredit, totCredit)}%</td>
                  </>
                )}
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, height: '16px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(totBarPct, 100)}%`, height: '100%', backgroundColor: COLORS.success, borderRadius: '8px' }} />
                    </div>
                    <span style={{ fontSize: '11px', minWidth: '32px', textAlign: 'right' }}>{totBarPct}%</span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // Pivot view: one row per SOV code, one column per discipline (piping split into
  // Shop/Field), each cell = that discipline's progress for the selected measure.
  // Keeps disciplines side-by-side instead of forcing one combined number, and never
  // sums piping shop+field credit. A blank cell means that discipline has no budget
  // in the selected measure for that SOV code (i.e. not yet distributed by the TC).
  const renderMatrix = () => {
    const discs = data.byDiscipline || [];
    const isCredit = sovViewMode === 'client' || matrixMeasure === 'credit';

    const columns = [];
    discs.forEach(d => {
      if (d.hasShopField) {
        columns.push({ key: `${d.discipline}__shop`, disc: d.discipline, phase: 'shop', label: d.discipline, sub: 'Shop' });
        columns.push({ key: `${d.discipline}__field`, disc: d.discipline, phase: 'field', label: d.discipline, sub: 'Field' });
      } else {
        columns.push({ key: d.discipline, disc: d.discipline, phase: null, label: d.discipline, sub: null });
      }
    });

    const lookup = {};
    discs.forEach(d => {
      lookup[d.discipline] = {};
      d.rows.forEach(r => { lookup[d.discipline][r.sov_code] = r; });
    });

    // Returns { has: bool, value: number } — has=false when no budget in this measure.
    const cellValue = (col, sovCode) => {
      const r = lookup[col.disc] && lookup[col.disc][sovCode];
      if (!r) return { has: false, value: 0 };
      let earned, total;
      if (col.phase === 'shop') {
        earned = isCredit ? r.earned_credit_shop : r.earned_hours_shop;
        total = isCredit ? r.total_credit : r.shop_budget_hrs;
      } else if (col.phase === 'field') {
        earned = isCredit ? r.earned_credit_field : r.earned_hours_field;
        total = isCredit ? r.total_credit : r.field_budget_hrs;
      } else {
        earned = isCredit ? r.earned_credit : r.earned_hrs;
        total = isCredit ? r.total_credit : r.total_budget_hrs;
      }
      const t = parseFloat(total) || 0;
      if (t <= 0) return { has: false, value: 0 };
      return { has: true, value: pct(earned, total) };
    };

    const rows = data.consolidated;

    const toggleBtn = (id, label) => (
      <button
        onClick={() => setMatrixMeasure(id)}
        style={{
          padding: '0.4rem 0.9rem',
          backgroundColor: matrixMeasure === id ? COLORS.primary : 'transparent',
          color: matrixMeasure === id ? 'white' : '#64748b',
          border: 'none', borderRadius: '6px', cursor: 'pointer',
          fontSize: '0.8rem', fontWeight: '600',
        }}
      >
        {label}
      </button>
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600' }}>Measure:</span>
          <div style={{ display: 'inline-flex', gap: '0.25rem', backgroundColor: 'white', borderRadius: '8px', padding: '0.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {toggleBtn('credit', 'Credit %')}
            {sovViewMode === 'internal' && toggleBtn('hours', 'Hours %')}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Blank (—) = no {isCredit ? 'credit' : 'hours'} budget distributed for that discipline yet.
          </span>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...thS, textAlign: 'left', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 1 }}>SOV Code</th>
                  <th style={thS}>Items</th>
                  {columns.map(col => (
                    <th key={col.key} style={{ ...thS, color: DISCIPLINE_COLORS[col.disc] || '#374151' }}>
                      {col.label}
                      {col.sub && <div style={{ fontSize: '9px', fontWeight: '600', opacity: 0.8 }}>{col.sub}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.sov_code + idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: '600', position: 'sticky', left: 0, backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc', color: r.sov_code === 'Unassigned' ? '#94a3b8' : '#1e293b' }}>
                      {r.sov_code}
                    </td>
                    <td style={{ ...tdS, color: '#64748b' }}>{fmtNum(r.total_items)}</td>
                    {columns.map(col => {
                      const cv = cellValue(col, r.sov_code);
                      return (
                        <td key={col.key} style={{ ...tdS, fontWeight: cv.has ? '600' : '400', color: cv.has ? getProgressColor(cv.value) : '#cbd5e1' }}>
                          {cv.has ? `${cv.value}%` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'consolidated', label: 'All Disciplines', icon: '📑' },
    { id: 'matrix', label: 'All Disciplines – 2', icon: '🧮' },
    ...disciplineTabs.map(d => ({ id: d.id, label: d.label, icon: '📋' })),
  ];

  const renderContent = () => {
    if (selectedView === 'consolidated') {
      return renderTable(data.consolidated, true, true);
    }
    if (selectedView === 'matrix') {
      return renderMatrix();
    }
    const disc = data.byDiscipline.find(d => d.discipline.toLowerCase() === selectedView);
    if (!disc) return null;
    return renderTable(disc.rows.map(r => ({ ...r, total_items: r.item_count })), false, !!disc.hasShopField);
  };

  const hasData = data && data.consolidated && data.consolidated.length > 0;

  return (
    <div>
      <div style={{
        marginBottom: '1.25rem',
        padding: '1.25rem',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)',
        borderRadius: '12px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: '700' }}>
            SOV Summary Report
          </h2>
          <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>
            Schedule of Values — client credits, or internal hours and credits, across all disciplines
          </p>
        </div>
        {hasData && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleDownloadPdf}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.target.style.backgroundColor = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.target.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
            >
              📥 Download PDF
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.target.style.backgroundColor = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.target.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
            >
              📧 Email Report
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Loading SOV data...</div>
          <div style={{ fontSize: '0.85rem' }}>Aggregating across all discipline trackers</div>
        </div>
      )}

      {error && (
        <div style={{ padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', textAlign: 'center' }}>
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {data.consolidated.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📑</div>
              <h3 style={{ margin: 0, color: COLORS.dark }}>No SOV Data Available</h3>
              <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>
                Import SOV codes in your discipline trackers (Mechanical, Vessels, Conveyors, Chutes, Skids) to see the summary report.
              </p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: 'auto' }}>
                  {sovViewMode === 'internal'
                    ? 'Internal view shows hours and credits. Shop and field credit are not added together.'
                    : 'Client view is credits only. Shop (fab) and field (install) are separate and are not added together.'}
                </span>
                <div style={{ display: 'inline-flex', gap: '0.25rem', backgroundColor: 'white', borderRadius: '8px', padding: '0.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  {[
                    { id: 'client', label: 'Client (Credits)' },
                    { id: 'internal', label: 'Internal (Hours + Credits)' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setSovViewMode(opt.id)}
                      style={{
                        padding: '0.4rem 0.9rem',
                        backgroundColor: sovViewMode === opt.id ? COLORS.primary : 'transparent',
                        color: sovViewMode === opt.id ? 'white' : '#64748b',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <MetricCard title="SOV Codes" value={data.summary.total_sov_codes} color={COLORS.primary} />
                <MetricCard title="Total Items" value={fmtNum(data.summary.total_items)} color={COLORS.info} />
                {sovViewMode === 'internal' && (
                  <>
                    <MetricCard title="Total Hours" value={fmtHrs(data.summary.total_budget_hrs)} color="#1e40af" />
                    <MetricCard title="Earned Hours" value={fmtHrs(data.summary.total_earned_hrs)} subtitle={`${data.summary.hrs_progress}% of budget`} color={COLORS.success} />
                  </>
                )}
                <MetricCard title="Total Credit" value={fmtHrs(data.summary.total_credit)} color="#7c3aed" />
                <MetricCard title="Credit Progress" value={`${data.summary.shop_credit_progress ?? 0}% / ${data.summary.field_credit_progress ?? 0}%`} subtitle="Shop (Fab) / Field (Install)" color="#059669" />
              </div>

              {tabs.length > 1 && (
                <div style={{
                  display: 'flex', gap: '0.25rem', marginBottom: '1rem',
                  backgroundColor: 'white', borderRadius: '12px', padding: '0.25rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}>
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setSelectedView(tab.id)}
                      style={{
                        padding: '0.6rem 1rem',
                        backgroundColor: selectedView === tab.id ? COLORS.primary : 'transparent',
                        color: selectedView === tab.id ? 'white' : '#64748b',
                        border: 'none', borderRadius: '8px', cursor: 'pointer',
                        fontSize: '0.85rem', fontWeight: '500',
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {renderContent()}
            </>
          )}
        </>
      )}

      {showEmailModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000,
        }} onClick={(e) => { if (e.target === e.currentTarget) { setShowEmailModal(false); setEmailResult(null); } }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '1.5rem',
            width: '450px', maxWidth: '90vw', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}>
            <h3 style={{ margin: '0 0 1rem', color: COLORS.dark, fontSize: '1.1rem' }}>
              📧 Email SOV Summary Report
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 1rem' }}>
              The report will be attached as a PDF. Enter one or more email addresses separated by commas.
            </p>
            <input
              type="text"
              value={emailAddresses}
              onChange={(e) => setEmailAddresses(e.target.value)}
              placeholder="email@example.com, another@example.com"
              style={{
                width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px',
                border: '1px solid #d1d5db', fontSize: '0.9rem', marginBottom: '1rem',
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmail(); }}
            />
            {emailResult && (
              <div style={{
                padding: '0.5rem 0.75rem', borderRadius: '8px', marginBottom: '1rem',
                fontSize: '0.85rem',
                backgroundColor: emailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: emailResult.type === 'success' ? '#059669' : '#dc2626',
              }}>
                {emailResult.message}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => { setShowEmailModal(false); setEmailResult(null); }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddresses.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: emailSending ? '#9ca3af' : '#3b82f6',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: emailSending ? 'wait' : 'pointer',
                  fontSize: '0.85rem', fontWeight: '500',
                }}
              >
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOVReport;
